import { Injectable } from '@angular/core';
import { bufferTime, Subject } from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class CacheService {

    private cache = new Map<string, any>();
    private maxCacheSize = 100 * 1024 * 1024; // 100 MB

    private cacheSizeManagerSubject: Subject<number> = new Subject<number>();

    constructor() {
        this.cacheSizeManagerSubject.pipe(
            bufferTime(5_000),
        ).subscribe((sizes: number[]) => {
            if (sizes && sizes.length > 0) {
                this.manageCacheSize(sizes.reduce((previous, current) => previous + current, 0));
            }
        })
    }

    /**
     * Set the `data` in the cache for a given `key`.
     * Note that there is no guarantee that the cache will be held for the given `ttl`.
     *
     * @param key Uniqueness identifier for the data.
     * @param data Data to be cached (Must be less than 100 MB).
     * @param ttl Duration in milliseconds after which the given data must be invalidated.
     *
     * @returns true if the caching was successful
     * */
    public setCache(key: string, data: any, ttl: number): boolean {
        const size = this.getObjectSize(data);
        if (size > this.maxCacheSize) {
            return false;
        }
        this.cacheSizeManagerSubject.next(size);
        this.cache.set(key, {
            data: data,
            size: size,
            timestamp: new Date().getTime(),
            expiry_timestamp: new Date().getTime() + ttl
        });
        return true;
    }

    public getCache(key: string): any {
        const cacheEntry = this.cache.get(key);
        if (cacheEntry) {
            const { data, expiry_timestamp } = cacheEntry;
            const currentTime = new Date().getTime();
            if (currentTime < expiry_timestamp) {
                return data;
            } else {
                this.cache.delete(key); // Invalidate expired cache
            }
        }
        return null;
    }

    private manageCacheSize(newEntrySize: number) {
        let currentSize = this.getCacheSize();
        // Clear the amount of new cache size required.
        while (currentSize + newEntrySize > this.maxCacheSize && this.cache.size > 0) {
            const oldestKey = this.getOldestCacheKey();
            if (oldestKey) {
                const entrySize = this.cache.get(oldestKey)?.size || 0;
                this.cache.delete(oldestKey);
                currentSize -= entrySize;
            }
        }
    }

    private getCacheSize(): number {
        let totalSize = 0;
        this.cache.forEach(entry => {
            totalSize += entry.size;
        });
        return totalSize;
    }

    private getOldestCacheKey(): string | undefined {
        let oldestKey: string | undefined;
        let oldestTimestamp = Infinity;

        this.cache.forEach((entry, key) => {
            if (entry.timestamp < oldestTimestamp) {
                oldestTimestamp = entry.timestamp;
                oldestKey = key;
            }
        });

        return oldestKey;
    }

    // Get current cache size
    private getObjectSize(obj: any): number {
        const jsonString = JSON.stringify(obj);
        return new Blob([jsonString]).size;
    }
}
